/**
 * Helpers compartilhados pelos gráficos analíticos do dashboard.
 * SVG-puro, sem libs externas. Todas as escalas/formatadores são determinísticos.
 */

export interface PointXY {
  x: number;
  y: number;
}

export interface Scale {
  toX(index: number, total: number): number;
  toY(value: number): number;
}

export interface ChartGeometry {
  width: number;
  height: number;
  padding: { top: number; right: number; bottom: number; left: number };
  innerWidth: number;
  innerHeight: number;
}

export function makeGeometry(
  width: number,
  height: number,
  padding: Partial<ChartGeometry["padding"]> = {},
): ChartGeometry {
  const p = {
    top: padding.top ?? 16,
    right: padding.right ?? 12,
    bottom: padding.bottom ?? 28,
    left: padding.left ?? 44,
  };
  return {
    width,
    height,
    padding: p,
    innerWidth: Math.max(1, width - p.left - p.right),
    innerHeight: Math.max(1, height - p.top - p.bottom),
  };
}

/** Formata data YYYY-MM-DD para DD/MM (curta). */
export function formatDateShort(date: string): string {
  if (!date || date.length < 10) return date;
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`;
}

/** Formata BRL compacto: 12.345 → "R$ 12k", 1.234.567 → "R$ 1,2M". */
export function formatBrlCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}R$ ${(abs / 1_000_000).toFixed(1).replace(".", ",").replace(/,0$/, "")}M`;
  if (abs >= 1_000) return `${sign}R$ ${(abs / 1_000).toFixed(1).replace(".", ",").replace(/,0$/, "")}k`;
  return `${sign}R$ ${Math.round(abs)}`;
}

/** Formata minutos compacto: 90 → "1,5h", 30 → "30min". */
export function formatMinutesCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 60) return `${sign}${(abs / 60).toFixed(1).replace(".", ",").replace(/,0$/, "")}h`;
  return `${sign}${Math.round(abs)}min`;
}

/**
 * Calcula um max "bonito" — arredondado pra cima ao múltiplo de uma potência de 10
 * apropriada. Ex: 173 → 200, 1850 → 2000. Garante mínimo > 0.
 */
export function niceMax(rawMax: number): number {
  if (!Number.isFinite(rawMax) || rawMax <= 0) return 1;
  const exponent = Math.floor(Math.log10(rawMax));
  const fraction = rawMax / Math.pow(10, exponent);
  let niceFraction: number;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * Math.pow(10, exponent);
}

/** Gera ticks uniformes entre 0 e max (inclusive), N entradas. */
export function ticks(max: number, count = 4): number[] {
  if (max <= 0) return [0];
  const step = max / count;
  const out: number[] = [];
  for (let i = 0; i <= count; i++) out.push(step * i);
  return out;
}

/** Filtra um array de strings de data pra mostrar no máximo `keep` rótulos. */
export function thinLabels<T>(items: T[], keep: number): boolean[] {
  if (items.length <= keep) return items.map(() => true);
  const stride = Math.ceil(items.length / keep);
  return items.map((_, i) => i % stride === 0);
}
